import { GeolocationPosition } from '@/types';

class GeolocationService {
  private watchId: number | null = null;
  private currentPosition: GeolocationPosition | null = null;
  private positionListeners: ((position: GeolocationPosition) => void)[] = [];
  private retryCount = 0;
  private maxRetries = 3;
  private retryTimeout: number | null = null;

  // Aggiungi listener per aggiornamenti posizione
  addPositionListener(listener: (position: GeolocationPosition) => void): void {
    this.positionListeners.push(listener);
  }

  // Rimuovi listener
  removePositionListener(listener: (position: GeolocationPosition) => void): void {
    this.positionListeners = this.positionListeners.filter(l => l !== listener);
  }

  // Notifica posizione a tutti i listener
  private notifyPosition(position: GeolocationPosition): void {
    this.positionListeners.forEach(listener => listener(position));
  }

  // Verifica se la geolocalizzazione è supportata
  isSupported(): boolean {
    return 'geolocation' in navigator;
  }

  // Ottieni posizione corrente
  async getCurrentPosition(): Promise<GeolocationPosition> {
    if (!this.isSupported()) {
      throw new Error('Geolocation is not supported by this browser');
    }

    return new Promise((resolve, reject) => {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 20000, // Aumentato a 20 secondi
        maximumAge: 300000 // 5 minuti
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const geoPosition: GeolocationPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy || 0,
            timestamp: position.timestamp
          };

          this.currentPosition = geoPosition;
          this.notifyPosition(geoPosition);
          resolve(geoPosition);
        },
        (error) => {
          let errorMessage = 'Unknown geolocation error';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Geolocation access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  // Avvia monitoraggio posizione
  startWatching(): void {
    if (!this.isSupported() || this.watchId !== null) {
      return;
    }

    this.startWatchingWithRetry();
  }

  // Verifica se il monitoraggio è attivo
  isWatching(): boolean {
    return this.watchId !== null;
  }

  // Avvia monitoraggio solo se non è già attivo
  startWatchingIfNeeded(): void {
    if (!this.isWatching()) {
      this.startWatching();
    }
  }

  // Avvia monitoraggio con retry automatico
  private startWatchingWithRetry(): void {
    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 30000, // Aumentato a 30 secondi
      maximumAge: 60000 // 1 minuto
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        // Reset retry count on success
        this.retryCount = 0;
        
        const geoPosition: GeolocationPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || 0,
          timestamp: position.timestamp
        };

        this.currentPosition = geoPosition;
        this.notifyPosition(geoPosition);
      },
      (error) => {
        // Log più pulito per errori comuni
        if (import.meta.env.MODE === 'development') {
          const errorMessage = error.message;
          if (errorMessage.includes('Position update is unavailable') || 
              errorMessage.includes('Location information unavailable')) {
            console.warn('Geolocation watch error:', errorMessage);
          } else {
            console.warn('Geolocation watch error:', error);
          }
        }
        this.handleWatchError(error);
      },
      options
    );
  }

  // Gestisce errori di geolocalizzazione con retry
  private handleWatchError(error: GeolocationPositionError): void {
    // Clear existing retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Check if we should retry
    if (this.retryCount < this.maxRetries && error.code === error.TIMEOUT) {
      this.retryCount++;
      const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount), 10000); // Exponential backoff, max 10s
      
      if (import.meta.env.MODE === 'development') {
        console.log(`Retrying geolocation in ${retryDelay}ms (attempt ${this.retryCount}/${this.maxRetries})`);
      }
      
      this.retryTimeout = setTimeout(() => {
        this.stopWatching();
        this.startWatchingWithRetry();
      }, retryDelay);
    } else {
      if (import.meta.env.MODE === 'development') {
        const errorMessage = error.message;
        if (errorMessage.includes('Position update is unavailable') || 
            errorMessage.includes('Location information unavailable')) {
          console.warn('Geolocation failed after maximum retries:', errorMessage);
        } else {
          console.error('Geolocation failed after maximum retries or non-timeout error:', error);
        }
      }
      this.retryCount = 0;
    }
  }

  // Ferma monitoraggio posizione
  stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    
    // Clear retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Reset retry count
    this.retryCount = 0;
  }

  // Reset completo del servizio (per logout)
  reset(): void {
    this.stopWatching();
    this.currentPosition = null;
    this.positionListeners = [];
    this.retryCount = 0;
    console.log('Geolocation service reset completato');
  }

  // Ottieni ultima posizione nota
  getLastKnownPosition(): GeolocationPosition | null {
    return this.currentPosition;
  }

  // Richiedi permessi geolocalizzazione
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      // Prova a ottenere la posizione per verificare i permessi
      await this.getCurrentPosition();
      return true;
    } catch (error) {
      return false;
    }
  }

  // Ottieni indirizzo da coordinate (reverse geocoding) con fallback multipli
  async getAddressFromCoordinates(latitude: number, longitude: number): Promise<string> {
    const fallbackAddress = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    
    // Lista di servizi di reverse geocoding da provare
    const geocodingServices = [
      {
        name: 'OpenStreetMap Nominatim',
        url: `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18`,
        headers: {
          'User-Agent': 'FieldApp/1.0 (Fondazione Roberta Mucera)',
        }
      },
      {
        name: 'BigDataCloud',
        url: `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=it`,
        headers: {}
      }
    ];

    for (const service of geocodingServices) {
      try {
        console.log(`Trying ${service.name} for reverse geocoding...`);
        
        // Timeout di 5 secondi per ogni servizio
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(service.url, {
          method: 'GET',
        headers: {
          'Accept': 'application/json',
          ...(service.headers || {})
        } as HeadersInit,
          mode: 'cors',
          cache: 'no-cache',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          console.warn(`${service.name} returned ${response.status}: ${response.statusText}`);
          continue;
        }

        const data = await response.json();
        const address = this.extractAddressFromResponse(data, service.name);
        
        if (address && address !== fallbackAddress) {
          console.log(`✅ Address found using ${service.name}: ${address}`);
          return address;
        }
        
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.warn(`${service.name} timed out after 5 seconds`);
        } else {
          console.warn(`${service.name} failed:`, error);
        }
        continue;
      }
    }
    
    console.warn('All reverse geocoding services failed, trying local fallback...');
    
    // Fallback locale per l'Italia
    try {
      const localAddress = this.getLocalAddressFallback(latitude, longitude);
      console.log(`✅ Local fallback address: ${localAddress}`);
      return localAddress;
    } catch (error) {
      console.warn('Local fallback also failed:', error);
      return fallbackAddress;
    }
  }

  // Estrai indirizzo dalla risposta del servizio
  private extractAddressFromResponse(data: any, serviceName: string): string | null {
    try {
      if (serviceName === 'OpenStreetMap Nominatim') {
        if (data.display_name) {
          return data.display_name;
        }
      } else if (serviceName === 'BigDataCloud') {
        if (data.localityInfo && data.localityInfo.administrative) {
          const parts = [];
          
          if (data.city) parts.push(data.city);
          if (data.principalSubdivision) parts.push(data.principalSubdivision);
          if (data.countryName) parts.push(data.countryName);
          
          return parts.join(', ');
        }
      }
    } catch (error) {
      console.warn(`Error extracting address from ${serviceName}:`, error);
    }
    
    return null;
  }

  // Fallback locale per reverse geocoding (funziona offline)
  private getLocalAddressFallback(latitude: number, longitude: number): string {
    // Coordinate approssimative delle principali città italiane
    const italianCities = [
      { name: 'Roma', lat: 41.9028, lon: 12.4964, region: 'Lazio' },
      { name: 'Milano', lat: 45.4642, lon: 9.1900, region: 'Lombardia' },
      { name: 'Napoli', lat: 40.8518, lon: 14.2681, region: 'Campania' },
      { name: 'Torino', lat: 45.0703, lon: 7.6869, region: 'Piemonte' },
      { name: 'Palermo', lat: 38.1157, lon: 13.3615, region: 'Sicilia' },
      { name: 'Genova', lat: 44.4056, lon: 8.9463, region: 'Liguria' },
      { name: 'Bologna', lat: 44.4949, lon: 11.3426, region: 'Emilia-Romagna' },
      { name: 'Firenze', lat: 43.7696, lon: 11.2558, region: 'Toscana' },
      { name: 'Bari', lat: 41.1177, lon: 16.8719, region: 'Puglia' },
      { name: 'Catania', lat: 37.5079, lon: 15.0830, region: 'Sicilia' },
      { name: 'Venezia', lat: 45.4408, lon: 12.3155, region: 'Veneto' },
      { name: 'Verona', lat: 45.4384, lon: 10.9916, region: 'Veneto' },
      { name: 'Messina', lat: 38.1938, lon: 15.5540, region: 'Sicilia' },
      { name: 'Padova', lat: 45.4064, lon: 11.8768, region: 'Veneto' },
      { name: 'Trieste', lat: 45.6495, lon: 13.7768, region: 'Friuli-Venezia Giulia' },
    ];

    // Trova la città più vicina
    let closestCity = italianCities[0];
    let minDistance = this.calculateDistance(latitude, longitude, closestCity.lat, closestCity.lon);

    for (const city of italianCities) {
      const distance = this.calculateDistance(latitude, longitude, city.lat, city.lon);
      if (distance < minDistance) {
        minDistance = distance;
        closestCity = city;
      }
    }

    // Se la distanza è ragionevole (entro 50km), usa il nome della città
    if (minDistance < 50000) {
      return `${closestCity.name}, ${closestCity.region}, Italia`;
    }

    // Altrimenti usa le coordinate
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  // Calcola distanza tra due punti (in metri)
  calculateDistance(
    lat1: number, 
    lon1: number, 
    lat2: number, 
    lon2: number
  ): number {
    const R = 6371e3; // Raggio della Terra in metri
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distanza in metri
  }

  // Verifica se la posizione è valida (entro un raggio ragionevole)
  isValidPosition(position: GeolocationPosition, maxAccuracy: number = 100): boolean {
    return (position.accuracy ?? Infinity) <= maxAccuracy;
  }

  // Ottieni posizione con fallback
  async getPositionWithFallback(): Promise<GeolocationPosition | null> {
    try {
      return await this.getCurrentPosition();
    } catch (error) {
      console.warn('Failed to get current position:', error);
      
      // Fallback: usa ultima posizione nota se disponibile
      const lastPosition = this.getLastKnownPosition();
      if (lastPosition && this.isValidPosition(lastPosition, 500)) {
        console.log('Using last known position as fallback');
        return lastPosition;
      }
      
      return null;
    }
  }

  // Ottieni posizione con retry automatico
  async getPositionWithRetry(maxRetries: number = 2): Promise<GeolocationPosition> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Solo log in modalità debug
        if (import.meta.env.MODE === 'development') {
          console.log(`Attempting to get position (attempt ${attempt}/${maxRetries})`);
        }
        return await this.getCurrentPosition();
      } catch (error) {
        lastError = error as Error;
        
        // Log più pulito per errori comuni in sviluppo
        if (import.meta.env.MODE === 'development') {
          const errorMessage = (error as Error).message;
          if (errorMessage.includes('Location information unavailable') || 
              errorMessage.includes('Position update is unavailable')) {
            console.warn(`Position attempt ${attempt} failed: ${errorMessage}`);
          } else {
            console.warn(`Position attempt ${attempt} failed:`, error);
          }
        }
        
        if (attempt < maxRetries) {
          const delay = 1000 * attempt; // Progressive delay
          if (import.meta.env.MODE === 'development') {
            console.log(`Retrying in ${delay}ms...`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError || new Error('Failed to get position after all retries');
  }
}

export const geolocationService = new GeolocationService();
export default geolocationService;
